import { useLocalStorage } from '@uidotdev/usehooks';
import CounterUnit from './CounterUnit';
import CustomDataField from './CustomDataField';
import TagSelectPopover from './TagSelectPopover';
import CandidatesBoardsMenu from './CandidatesBoardsMenu';
import { Tag, type TagColor } from '@/components/Tag';
import type { CustomField } from '../../custom_categories/hooks/useCustomFields';
import { Fragment } from 'react';
import type {
  AgentApiCandidateProfilesItem,
  AgentApiCandidateProfilesBoard,
} from '@/types/serializers';
import { Link } from '@tanstack/react-router';
import { MaterialIcon } from '@/components/MaterialIcon';
import Checkbox from '@/components/Checkbox';
import UsersMenu from '../../components/UsersMenu';
import type { ActiveUser } from '../../types/ActiveUser';

// 文字数がmaxを超える場合に省略記号を付与して切り詰める
const truncateText = (text: string, max: number) => {
  return text.length > max ? text.slice(0, max) + '…' : text;
};

interface Props extends AgentApiCandidateProfilesItem {
  customFields: CustomField[];
  activeUsers: ActiveUser[];
  currentUserId?: number;
  onUpdateAssignees: (candidateProfileId: number, userIds: number[]) => void;
  candidatesBoards: AgentApiCandidateProfilesBoard[];
  onBoardChanged: () => void;
  isChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
}

export default function CandidateCard({
  id,
  name,
  age,
  company_name,
  job_position,
  custom_data,
  organization_users,
  counter_cache,
  candidate_board,
  customFields,
  activeUsers,
  currentUserId,
  onUpdateAssignees,
  candidatesBoards,
  onBoardChanged,
  tags,
  created_at,
  isChecked,
  onCheckedChange,
}: Props) {
  const [visibleCustomFieldIds] = useLocalStorage<number[]>('visibleCustomFieldIds', []);

  const selectedUserIds = organization_users?.map((u) => u.id) ?? [];

  const handleSelectAssignees = (userIds: number[]) => {
    onUpdateAssignees(id, userIds);
  };

  return (
    <div className="flex gap-4 rounded-sm bg-white p-xl shadow-layer2">
      {/* チェックボックス */}
      <div className="flex shrink-0 items-start pt-1" onClick={(e) => e.stopPropagation()}>
        <Checkbox checked={isChecked} onChange={(checked) => onCheckedChange?.(checked)} />
      </div>
      {/* カードの内容 */}
      <div className="flex w-full min-w-0 flex-col gap-y-lg">
        <div className="grid grid-cols-[minmax(206px,1fr)_minmax(min-content,440px)] gap-4 text-main">
          <div className="flex min-w-0 flex-col justify-between gap-2">
            <div className="flex min-w-0 flex-wrap items-center gap-4">
              <Link
                to="/agent/candidates/$candidateProfileId"
                params={{ candidateProfileId: id.toString() }}
                className="flex min-w-0 items-center gap-xxs text-heading-sm font-bold text-main"
              >
                <span className="truncate">{name}</span>
                {age && <span className="shrink-0 text-lg font-bold">{'(' + age + ')'}</span>}
              </Link>
              <div
                className="shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
              >
                <UsersMenu
                  activeUsers={activeUsers}
                  selectedUserIds={selectedUserIds}
                  onSelect={handleSelectAssignees}
                  currentUserId={currentUserId}
                />
              </div>
              <div
                className="shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
              >
                <CandidatesBoardsMenu
                  candidatesBoards={candidatesBoards}
                  candidateProfileId={id}
                  candidateBoard={
                    candidate_board
                      ? {
                          id: candidate_board.id,
                          title: candidate_board.title,
                          colorType: candidate_board.color_type,
                        }
                      : undefined
                  }
                  onBoardChanged={onBoardChanged}
                />
              </div>
            </div>
            <div className="flex gap-5">
              {company_name && (
                <div className="flex items-center gap-2">
                  <MaterialIcon name="apartment" className="text-xl" />
                  <div className="group relative inline-block">
                    <p className="text-xs">{truncateText(company_name, 11)}</p>
                    {company_name.length > 11 && (
                      <div className="absolute top-full left-1/2 z-10 mt-2 hidden w-max -translate-x-1/2 transform rounded bg-black p-2 text-xs text-white group-hover:block">
                        {company_name}
                        <div className="absolute -top-3 left-1/2 size-0 -translate-x-1/2 border-8 border-solid border-transparent border-b-black"></div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {job_position && (
                <div className="flex items-center gap-2">
                  <MaterialIcon name="work" className="text-xl" />
                  <div className="group relative inline-block">
                    <p className="text-xs">{truncateText(job_position, 11)}</p>
                    {job_position.length > 11 && (
                      <div className="absolute top-full left-1/2 z-10 mt-2 hidden w-max -translate-x-1/2 transform rounded bg-black p-2 text-xs text-white group-hover:block">
                        {job_position}
                        <div className="absolute -top-3 left-1/2 size-0 -translate-x-1/2 border-8 border-solid border-transparent border-b-black"></div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="grid min-w-0 grid-cols-5 items-start gap-2.5">
            <CounterUnit count={counter_cache.count_of_suggest} name="提案中" />
            <CounterUnit count={counter_cache.count_of_document_screening} name="書類選考" />
            <CounterUnit
              count={
                counter_cache.count_of_interview +
                counter_cache.count_of_meeting +
                counter_cache.count_of_offer_meeting
              }
              name="選考中"
            />
            <CounterUnit count={counter_cache.count_of_offer} name="内定" />
            <CounterUnit count={counter_cache.count_of_close} name="内定承諾" />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {tags.map((tag) => (
            <Tag key={tag.id} color={tag.color_hex as TagColor}>
              {tag.name}
            </Tag>
          ))}
          <TagSelectPopover id={id} tags={tags} />
        </div>
        <div className="flex flex-wrap gap-x-md gap-y-sm">
          {customFields
            .filter((field) => field.name !== '勤務会社' && field.name !== 'ポジション')
            .filter((field) => visibleCustomFieldIds.includes(field.id))
            .map((field, index) => (
              <Fragment key={field.id}>
                {index > 0 && (
                  <span className={'border-y-0 border-r border-l-0 border-solid border-border'} />
                )}
                <CustomDataField
                  field={field.name}
                  content={custom_data.find((data) => data.custom_field.id === field.id)?.content}
                  unit={field.unitName}
                />
              </Fragment>
            ))}
          {visibleCustomFieldIds.includes(100) && (
            <>
              <span className={'border-y-0 border-r border-l-0 border-solid border-border'} />
              <CustomDataField field="登録日" content={created_at} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
